import { Loadable, OrderExchangeData } from '@eih-netbroker/common';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { useFormatter } from '../../../../../shared/services/utils/useFormatter';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import classes from './OrderCurrencyExchangePaymentModeSelector.module.scss';
import { OrderCurrencyExchangeSelectionCard } from './OrderCurrencyExchangeSelectionCard';

export type OrderCurrencyExchangePaymentModeSelectorProps = OrderExchangeData & {
  orderRequestLoading: boolean;
  sourceComponent: OrderFormSourceComponent;
};

export const OrderCurrencyExchangePaymentModeSelector = ({
  fundingMode,
  setFundingMode,
  funding,
  isEdit,
  orderRequestLoading,
  sourceComponent,
}: OrderCurrencyExchangePaymentModeSelectorProps) => {
  const { translation } = useLocale();
  const t = translation.orderModal.currencyExchangeForm;
  const { formatNumberRoundOrF2 } = useFormatter();

  return (
    <div className={classes.container}>
      <span
        data-testid="order-currency-exchange-payment-mode-label"
        className={classes.label}
      >
        {t.paymentMode}
      </span>
      <div className={classes.row}>
        <OrderCurrencyExchangeSelectionCard
          isSelected={fundingMode === 'missingAmount'}
          onClick={() => setFundingMode && setFundingMode('missingAmount')}
          disabled={Loadable.hasValue(funding) && funding.missingAmount === 0}
          readOnly={orderRequestLoading}
          className={classes.card}
        >
          <span
            data-testid="order-currency-exchange-payment-mode-missing-amount-label"
            className={cssWhen(classes.cardTitle, [fundingMode === 'missingAmount', classes.isSelectedText])}
          >
            {t.onlyMissing}
          </span>
          <span className={classes.cardAmount}>
            <MoneyMaskHOC
              isInverse
              size={100}
              bypass={sourceComponent === OrderFormSourceComponent.Modal}
            >
              <NbIndefiniteLoaderValueWrapper
                showLoaderOnUninitialized
                loadableValue={funding}
                loadedValueCb={value => (
                  <span
                    data-testid="order-currency-exchange-payment-mode-missing-amount-value"
                    className={cssWhen([fundingMode === 'missingAmount', classes.isSelectedText])}
                  >
                    {value.missingAmount === 0
                      ? t.disabledValue
                      : `${formatNumberRoundOrF2(value.missingAmount)} ${value.targetCurrency}`}
                  </span>
                )}
              />
            </MoneyMaskHOC>
          </span>
        </OrderCurrencyExchangeSelectionCard>
        <OrderCurrencyExchangeSelectionCard
          isSelected={fundingMode === 'fullAmount'}
          onClick={() => setFundingMode && setFundingMode('fullAmount')}
          className={classes.card}
          readOnly={orderRequestLoading}
        >
          <span
            data-testid="order-currency-exchange-payment-mode-full-amount-label"
            className={cssWhen(classes.cardTitle, [fundingMode === 'fullAmount', classes.isSelectedText])}
          >
            {isEdit ? t.fullDifference : t.fullAmount}
          </span>
          <span className={classes.cardAmount}>
            <NbIndefiniteLoaderValueWrapper
              showLoaderOnUninitialized
              loadableValue={funding}
              loadedValueCb={value => (
                <span
                  data-testid="order-currency-exchange-payment-mode-full-amount-value"
                  className={cssWhen([fundingMode === 'fullAmount', classes.isSelectedText])}
                >
                  {formatNumberRoundOrF2(isEdit ? value.remaining.total : value.total.total)} {value.targetCurrency}
                </span>
              )}
            />
          </span>
        </OrderCurrencyExchangeSelectionCard>
      </div>
    </div>
  );
};
